/**
 * Client-side metric recalculation from a filtered trade array.
 * Used when the All/Long/Short toggle filters trades — only trade-based
 * metrics can be recomputed; equity-curve-dependent metrics show N/A.
 */

import { readChargeComponents } from './backtestTrades';

const asNum = (v, fb = 0) => { const n = Number(v); return Number.isFinite(n) ? n : fb; };

/** Metrics that CANNOT be computed from filtered trades (require full equity curve). */
export const EQUITY_CURVE_METRICS = new Set([
    'sharpe_ratio', 'sortino_ratio',
    'max_drawdown', 'max_dd_start', 'max_dd_end', 'max_dd_duration_days', 'max_dd_recovered',
    'calmar_ratio',
    'buy_hold_return', 'buy_hold_return_inr',
]);

/**
 * Recalculate metrics from a filtered trade array.
 * @param {Array} trades — normalized trade objects (side already uppercase)
 * @param {number} initialCapital
 * @returns {Object} metrics dict matching backend shape (equity-curve metrics set to null)
 */
export const recalcMetricsFromTrades = (trades = [], initialCapital = 100000) => {
    if (!trades.length) {
        return {
            total_trades: 0, winning_trades: 0, losing_trades: 0, win_rate: 0,
            total_profit: 0, total_loss: 0, profit_factor: null,
            avg_win: 0, avg_loss: 0, avg_pnl: 0, median_pnl: 0,
            gross_profit: 0, gross_loss: 0,
            largest_win: null, largest_loss: null, ratio_avg_win_loss: null,
            expectancy: 0,
            max_consecutive_wins: 0, max_consecutive_losses: 0,
            total_commission: 0, total_brokerage: 0, total_statutory_charges: 0,
            total_lots_traded: 0, skipped_trades_count: 0, capital_exhausted: false,
            // Equity-curve-dependent — null when filtered
            sharpe_ratio: null, sortino_ratio: null,
            max_drawdown: null, max_dd_start: null, max_dd_end: null,
            max_dd_duration_days: null, max_dd_recovered: null,
            calmar_ratio: null, buy_hold_return: null, buy_hold_return_inr: null,
        };
    }

    const pnls = trades.map(t => asNum(t.pnl));
    const wins = trades.filter(t => asNum(t.pnl) > 0);
    const losses = trades.filter(t => asNum(t.pnl) <= 0);

    const totalProfit = wins.reduce((s, t) => s + asNum(t.pnl), 0);
    const totalLoss = Math.abs(losses.reduce((s, t) => s + asNum(t.pnl), 0));
    const winRate = (wins.length / trades.length) * 100;
    const profitFactor = totalLoss > 0 ? totalProfit / totalLoss : null;

    const avgWin = wins.length ? totalProfit / wins.length : 0;
    const avgLoss = losses.length ? totalLoss / losses.length : 0;
    const avgPnl = pnls.reduce((a, b) => a + b, 0) / pnls.length;

    // Median
    const sorted = [...pnls].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const medianPnl = sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;

    // Gross (pre-charge) - API-provided per-trade `grossPnl`; null (Unresolved) when any trade lacks it.
    const sumOrNull = (rows, pick) => (
        rows.every(t => Number.isFinite(pick(t)) && pick(t) !== null)
            ? rows.reduce((s, t) => s + pick(t), 0) : null
    );
    const grossWins = sumOrNull(wins, t => t.grossPnl);
    const grossLosses = sumOrNull(losses, t => t.grossPnl);
    const grossProfit = grossWins;
    const grossLoss = grossLosses === null ? null : Math.abs(grossLosses);

    // Largest
    const largestWin = wins.length ? Math.max(...wins.map(t => asNum(t.pnl))) : null;
    const largestLoss = losses.length ? Math.min(...losses.map(t => asNum(t.pnl))) : null;

    // Ratio
    const ratioAvgWinLoss = avgLoss > 0 ? avgWin / avgLoss : null;

    // Expectancy
    const wr = winRate / 100;
    const expectancy = (avgWin * wr) - (avgLoss * (1 - wr));

    // Consecutive streaks
    let maxConsWins = 0, maxConsLosses = 0, curWin = 0, curLoss = 0;
    for (const t of trades) {
        if (asNum(t.pnl) > 0) { curWin++; curLoss = 0; maxConsWins = Math.max(maxConsWins, curWin); }
        else { curLoss++; curWin = 0; maxConsLosses = Math.max(maxConsLosses, curLoss); }
    }

    // Commission aggregates
    const totalCommission = trades.reduce((s, t) => s + asNum(t.commission), 0);
    const totalBrokerage = trades.reduce((s, t) => s + asNum(readChargeComponents(t.commission_breakdown).brokerage), 0);
    const totalStatutory = sumOrNull(trades, t => t.statutoryCharges);
    const totalLots = trades.reduce((s, t) => s + asNum(t.qty_lots ?? t.qtyLots, 1), 0);

    // Net P&L for return calc
    const netPnl = pnls.reduce((a, b) => a + b, 0);

    return {
        total_trades: trades.length,
        winning_trades: wins.length,
        losing_trades: losses.length,
        win_rate: Math.round(winRate * 100) / 100,
        profit_factor: profitFactor != null ? Math.round(profitFactor * 100) / 100 : null,
        total_profit: Math.round(totalProfit * 100) / 100,
        total_loss: Math.round(totalLoss * 100) / 100,
        avg_win: Math.round(avgWin * 100) / 100,
        avg_loss: Math.round(avgLoss * 100) / 100,
        avg_pnl: Math.round(avgPnl * 100) / 100,
        median_pnl: Math.round(medianPnl * 100) / 100,
        gross_profit: grossProfit === null ? null : Math.round(grossProfit * 100) / 100,
        gross_loss: grossLoss === null ? null : Math.round(grossLoss * 100) / 100,
        largest_win: largestWin != null ? Math.round(largestWin * 100) / 100 : null,
        largest_loss: largestLoss != null ? Math.round(largestLoss * 100) / 100 : null,
        ratio_avg_win_loss: ratioAvgWinLoss != null ? Math.round(ratioAvgWinLoss * 1000) / 1000 : null,
        expectancy: Math.round(expectancy * 100) / 100,
        max_consecutive_wins: maxConsWins,
        max_consecutive_losses: maxConsLosses,
        total_commission: Math.round(totalCommission * 100) / 100,
        total_brokerage: Math.round(totalBrokerage * 100) / 100,
        total_statutory_charges: totalStatutory === null ? null : Math.round(totalStatutory * 100) / 100,
        total_lots_traded: totalLots,
        skipped_trades_count: 0,
        capital_exhausted: false,
        net_profit: Math.round(netPnl * 100) / 100,
        total_return_pct: initialCapital > 0 ? Math.round((netPnl / initialCapital) * 10000) / 100 : 0,
        // Equity-curve-dependent — not computable from filtered trades
        sharpe_ratio: null,
        sortino_ratio: null,
        max_drawdown: null,
        max_dd_start: null,
        max_dd_end: null,
        max_dd_duration_days: null,
        max_dd_recovered: null,
        calmar_ratio: null,
        buy_hold_return: null,
        buy_hold_return_inr: null,
    };
};
